import type { ISupportDiagnosticsPerformance } from '@srm/screen-sharing/light';

/** Последние долгие задачи главного потока в пакете диагностики (§43.2). */
export const SUPPORT_LONG_TASKS_RECENT = 20;

const tasks = { count: 0, totalMs: 0, maxMs: 0, recent: [] as { at: string; ms: number }[] };
let installed = 0;
let observer: PerformanceObserver | undefined;

const round = (value: number) => Math.round(value);

/** Долгая задача (`longtask`, ≥ 50 мс) — в счётчики и в кольцо последних. */
export function recordLongTask(durationMs: number, startTime: number): void {
    tasks.count++;
    tasks.totalMs += durationMs;
    tasks.maxMs = Math.max(tasks.maxMs, durationMs);
    const at = new Date(performance.timeOrigin + startTime).toISOString();
    tasks.recent.push({ at, ms: round(durationMs) });
    if (tasks.recent.length > SUPPORT_LONG_TASKS_RECENT)
        tasks.recent.splice(0, tasks.recent.length - SUPPORT_LONG_TASKS_RECENT);
}

/**
 * Наблюдатель долгих задач (Chromium; в остальных браузерах `longtask` нет — раздел остаётся с нулями) — с начала
 * страницы (`buffered`). Ставится вместе с журналом ошибок страницы; возвращает отписку.
 */
export function installSupportLongTasks(): () => void {
    if (installed++ === 0 && typeof PerformanceObserver !== 'undefined') {
        try {
            observer = new PerformanceObserver((list) =>
                list.getEntries().forEach((entry) => recordLongTask(entry.duration, entry.startTime)),
            );
            observer.observe({ type: 'longtask', buffered: true });
        } catch {
            observer = undefined;
        }
    }
    let attached = true;
    return () => {
        if (!attached) return;
        attached = false;
        if (--installed === 0) {
            observer?.disconnect();
            observer = undefined;
        }
    };
}

/** Тайминги загрузки страницы (Navigation Timing 2), мс. */
function navigationTiming(): ISupportDiagnosticsPerformance['navigation'] {
    const entry = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!entry) return undefined;
    const value = (ms: number) => (ms > 0 ? round(ms) : undefined);
    const timing = {
        ttfbMs: value(entry.responseStart - entry.requestStart),
        domContentLoadedMs: value(entry.domContentLoadedEventEnd),
        loadMs: value(entry.loadEventEnd),
    };
    return Object.values(timing).some((item) => item !== undefined) ? timing : undefined;
}

/** Раздел `performance` пакета: время жизни вкладки, долгие задачи и загрузка страницы. */
export function supportPerformance(): ISupportDiagnosticsPerformance {
    const navigation = navigationTiming();
    return {
        uptimeSec: round(performance.now() / 1000),
        longTasks: {
            count: tasks.count,
            totalMs: round(tasks.totalMs),
            maxMs: round(tasks.maxMs),
            recent: tasks.recent.slice(),
        },
        ...(navigation ? { navigation } : {}),
    };
}
